import debounce from "lodash.debounce";
import groupBy from "lodash.groupby";
import {
  ArrowBigUpDash,
  ArrowDownUp,
  CornerDownLeft,
  Search,
} from "lucide-react";
import { useContext, useEffect, useLayoutEffect, useRef } from "react";
import { useImmer } from "use-immer";

import { IgButton } from "@intelligems/ig-theme/IgButton";
import { IgDialog } from "@intelligems/ig-theme/IgDialog";
import { IgDivider } from "@intelligems/ig-theme/IgDivider";
import { IgTextField } from "@intelligems/ig-theme/IgTextField";
import { IgTypography } from "@intelligems/ig-theme/IgTypography";
import { Stack, useTheme } from "@mui/material";
import { useDialog } from "@src/hooks/dialog";
import { mpTrackCmdK } from "@src/utils/mixpanel";

import { useKeyboardListener, useOptions } from "./hooks";
import { CmdKContext, CmdKState, SearchOption } from "./types";

interface OptionProps {
  option: SearchOption;
  optionIndex: number;
  refCallback: (e: HTMLButtonElement | null) => void;
}

const Option = ({ option, optionIndex, refCallback }: OptionProps) => {
  const {
    select,
    state: { highlighted },
  } = useContext(CmdKContext);
  const theme = useTheme();
  const selected = highlighted === optionIndex;

  return (
    <IgButton
      disableRipple
      onClick={() => {
        select(optionIndex);
      }}
      ref={refCallback}
      size="small"
      sx={{
        justifyContent: "left",

        ...(selected && {
          outlineColor: theme.palette.accent.main,
          outlineOffset: "-2px",
          outlineStyle: "solid",
          outlineWidth: "2px",
        }),
      }}
      tabIndex={0}
      variant="outlined"
    >
      <Stack padding={"1px"} direction={"row"}>
        {option.render()}
      </Stack>
    </IgButton>
  );
};

const tips = [
  {
    icon: <ArrowDownUp size={16} />,
    text: "Highlight",
  },
  {
    icon: <CornerDownLeft size={16} />,
    text: "Select",
  },
  {
    icon: <ArrowBigUpDash size={16} />,
    text: "Secondary Action",
  },
  {
    icon: (
      <>
        <ArrowBigUpDash size={16} />
        <CornerDownLeft size={16} />
      </>
    ),
    text: "Select Secondary Action",
  },
];

const Tips = () => {
  return (
    <Stack direction={"row"} gap={4}>
      {tips.map((tip, idx) => (
        <Stack alignItems={"center"} direction={"row"} gap={1} key={idx}>
          {tip.icon}
          <IgTypography>{tip.text}</IgTypography>
        </Stack>
      ))}
    </Stack>
  );
};

const CmdK = () => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const refs = useRef<Map<number, HTMLButtonElement>>(new Map());
  useKeyboardListener();

  const {
    state: { highlighted },
    setInput,
    options,
  } = useContext(CmdKContext);

  const groupedOptions = groupBy(
    options.map((option, idx) => [idx, option] as const),
    ([_, option]) => option.group
  );

  useLayoutEffect(() => {
    if (!containerRef.current || highlighted === undefined) {
      return;
    }

    const el = refs.current.get(highlighted);
    if (!el) {
      return;
    }

    const containerRect = containerRef.current.getBoundingClientRect();
    const elRect = el.getBoundingClientRect();

    const isInView =
      elRect.top >= containerRect.top && elRect.bottom <= containerRect.bottom;

    if (!isInView) {
      el.scrollIntoView({
        behavior: "smooth",
        block: elRect.top < containerRect.top ? "start" : "end",
      });
    }
  }, [highlighted]);

  return (
    <Stack gap={2}>
      <IgTextField
        placeholder="Search for tests or experiences..."
        variant="outlined"
        autoFocus
        fullWidth={true}
        slotProps={{
          input: {
            startAdornment: <Search size={16} style={{ marginRight: 8 }} />,
          },
        }}
        onChange={(e) => {
          setInput(e.target.value);
        }}
        sx={{
          marginBottom: 2,
        }}
      />

      <Stack
        ref={containerRef}
        sx={{
          gap: 4,
          maxHeight: "500px",
          overflow: "scroll",
        }}
      >
        {Object.entries(groupedOptions).map(([groupLabel, options]) => {
          if (!options.length) {
            return null;
          }
          return (
            <Stack gap={2} key={groupLabel}>
              <IgTypography
                sx={{
                  fontWeight: 500,
                }}
              >
                {groupLabel}
              </IgTypography>

              <Stack gap={1.5}>
                {options.map(([optionIdx, option], idx) => (
                  <Option
                    key={idx}
                    optionIndex={optionIdx}
                    option={option}
                    refCallback={(e) => {
                      if (e) {
                        refs.current.set(optionIdx, e);
                      } else {
                        refs.current.delete(optionIdx);
                      }
                    }}
                  />
                ))}
              </Stack>
            </Stack>
          );
        })}
      </Stack>

      <IgDivider />
      <Tips />
    </Stack>
  );
};

export const CmdKDialog = () => {
  const { isOpen, close: dialogClose } = useDialog("cmdk");
  const [state, setState] = useImmer<CmdKState>({});
  // TODO: it would be great if options construction could use
  // the shared context;
  const options = useOptions({
    state,
    query: state.input ? state.input : undefined,
    limit: 5,
  });
  const setInput = debounce(
    (input: string) => {
      setState((prev) => {
        prev.input = input;
      });
    },
    50,
    {
      trailing: true,
    }
  );
  const close = (track?: boolean) => {
    dialogClose();
    setInput.cancel();
    setState({});
    if (track) {
      mpTrackCmdK({
        action: "closed",
      });
    }
  };
  const select = (idx?: number) => {
    let option: (typeof options)[number] | undefined = undefined;
    if (idx !== undefined) {
      option = options[idx];
    } else if (state.highlighted !== undefined) {
      option = options[state.highlighted];
    } else {
      return;
    }

    if (option) {
      option.select();
      close();
      mpTrackCmdK({
        action: "selected",
      });
    }
  };

  useEffect(() => {
    if (isOpen) {
      mpTrackCmdK({
        action: "opened",
      });
    }
  }, [isOpen]);

  return (
    <IgDialog
      showDialog={isOpen}
      handleClose={() => close(true)}
      title=""
      hideActions={true}
      showCloseIcon={false}
      fullWidth={true}
      maxWidth="md"
      dontUseDialogContent={true}
      disableEscapeKeyDown={true}
      padding="16px"
      rawContent={
        <CmdKContext.Provider
          value={{
            close,
            options,
            select,
            setInput,
            setState,
            state,
          }}
        >
          <CmdK />
        </CmdKContext.Provider>
      }
    />
  );
};
